import React, { createContext, useContext, useState, PropsWithChildren } from "react";

/**
 * Interface for the role context value
 */
interface RoleContextValue {
	// The actual role from the server (Admin, Manager, Client, etc.)
	actualRole: string;
	// The effective role for rendering (can be overridden by admins to emulate any role)
	effectiveRole: string;
	// The role currently being emulated, or null if not emulating
	emulatedRole: string | null;
	// Set an emulated role (null to stop emulating); only works for Admins
	setEmulatedRole: (role: string | null) => void;
	// Set the actual role (called on login/load)
	setActualRole: (role: string) => void;
	// Check if user is actually an Admin (regardless of emulation)
	isActuallyAdmin: () => boolean;
}

const RoleContext = createContext<RoleContextValue | undefined>(undefined);

/**
 * Provider component for role context - manages actual role and client mode toggle
 */
export function RoleProvider({ children }: PropsWithChildren<{}>) {
	const [actualRole, setActualRole] = useState<string>("");
	const [emulatedRole, setEmulatedRoleState] = useState<string | null>(null);

	// Returns true for roles that have full admin/manager access
	const isAdminLike = (role: string) => role === "Admin" || role === "Manager";

	// Effective role is the emulated role when set by an Admin, otherwise the actual role
	const effectiveRole = emulatedRole !== null && actualRole === "Admin" ? emulatedRole : actualRole;

	const setEmulatedRole = (role: string | null) => {
		// Only Admins can emulate other roles
		if (actualRole === "Admin") {
			setEmulatedRoleState(role);
		}
	};

	// isActuallyAdmin is true only for real Admins (not managers), regardless of emulation
	const isActuallyAdmin = () => actualRole === "Admin";

	const value: RoleContextValue = {
		actualRole,
		effectiveRole,
		emulatedRole,
		setEmulatedRole,
		setActualRole,
		isActuallyAdmin,
	};

	return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

/**
 * Returns true if the given role has full admin-level access (Admin or Manager)
 */
export function isAdminRole(role: string): boolean {
	return role === "Admin" || role === "Manager";
}

/**
 * Hook to access the role context
 */
export function useRole(): RoleContextValue {
	const context = useContext(RoleContext);
	if (context === undefined) {
		throw new Error("useRole must be used within a RoleProvider");
	}
	return context;
}
